import { isAddress, type Address } from "viem";

// Reject any env value that isn't a proper checksum-or-lowercase EVM address.
// A typo in one character used to silently produce a 42-char wrong-target
// address and we'd happily send ETH to it. (Audit fix M3.)
const envAddr = (key: string): Address | undefined => {
    const v = import.meta.env[key];
    if (!v) return undefined;
    if (!isAddress(v)) {
        // Surface noisily during dev — silent failures here are how funds
        // end up at the wrong contract on mainnet.
        // eslint-disable-next-line no-console
        console.error(`[config] ${key} is not a valid EVM address: ${v}`);
        return undefined;
    }
    return v;
};

export const ADDRESSES = {
    PIGGY_TOKEN:         envAddr("VITE_PIGGY_TOKEN_ADDRESS"),
    PIGGY_PAIR:          envAddr("VITE_PIGGY_PAIR_ADDRESS"),
    TAX_COLLECTOR:       envAddr("VITE_TAX_COLLECTOR_ADDRESS"),
    LP_MANAGER:          envAddr("VITE_LP_MANAGER_ADDRESS"),
    REWARD_DISTRIBUTOR:  envAddr("VITE_REWARD_DISTRIBUTOR_ADDRESS"),
    LOCKED_LP_STAKING:   envAddr("VITE_LOCKED_LP_STAKING_ADDRESS"),
    PIGGY_STAKING:       envAddr("VITE_PIGGY_STAKING_ADDRESS"),
    REFLECTION:          envAddr("VITE_REFLECTION_ADDRESS"),
    REFERRAL_REGISTRY:   envAddr("VITE_REFERRAL_REGISTRY_ADDRESS"),
    TREASURY:            envAddr("VITE_TREASURY_ADDRESS"),
    PIGGY_ORIGINALS:     envAddr("VITE_PIGGY_ORIGINALS_ADDRESS"),
    PIGGY_ACHIEVEMENTS:  envAddr("VITE_PIGGY_ACHIEVEMENTS_ADDRESS"),
    DEX_ROUTER:          envAddr("VITE_DEX_ROUTER"),
    WNATIVE:             envAddr("VITE_WNATIVE"),
} as const;

// No silent mainnet fallback — a missing env on a Netlify deploy used to
// let mainnet wallets through the chain guard while every address still
// pointed at testnet contracts. (Audit fix H6.)
const rawChainId = import.meta.env.VITE_CHAIN_ID;
if (!rawChainId) {
    throw new Error(
        "VITE_CHAIN_ID is required. Set it to 46630 for Robinhood Chain testnet or 4663 for mainnet.",
    );
}
export const CHAIN_ID = Number(rawChainId);
if (!Number.isFinite(CHAIN_ID) || (CHAIN_ID !== 4663 && CHAIN_ID !== 46630)) {
    throw new Error(`VITE_CHAIN_ID must be 4663 or 46630, got ${rawChainId}`);
}

export const IS_TESTNET = CHAIN_ID === 46630;
export const EXPLORER_BASE = IS_TESTNET
    ? "https://explorer.testnet.chain.robinhood.com"
    : "https://robinhoodchain.blockscout.com";
export const EXPLORER_TX = `${EXPLORER_BASE}/tx`;
export const EXPLORER_ADDR = `${EXPLORER_BASE}/address`;
export const EXPLORER_TOKEN = `${EXPLORER_BASE}/token`;

// NFT links. No established marketplace (Element, tofuNFT, OpenSea, ...)
// indexes Robinhood Chain yet, so we link to the Blockscout token page,
// which renders holders/inventory for any ERC-721 with a tokenURI.
export const marketplaceCollectionUrls = (contract?: string) => {
    if (!contract) return null;
    return {
        explorer: `${EXPLORER_TOKEN}/${contract}`,
    };
};

export const marketplaceTokenUrls = (contract?: string, tokenId?: bigint | number) => {
    if (!contract || tokenId === undefined) return null;
    const id = tokenId.toString();
    return {
        explorer: `${EXPLORER_BASE}/token/${contract}/instance/${id}`,
    };
};

// Uniswap deep links. NOTE: Robinhood Chain (4663) is not listed in
// Uniswap's sdk-core chains.ts as of 2026-07, so there is no verifiable
// `?chain=` slug yet — we omit the chain param; the Uniswap app follows
// the connected wallet's chain.
const UNI_BASE = "https://app.uniswap.org";

export const dexSwapUrl = (token?: string) =>
    token ? `${UNI_BASE}/swap?outputCurrency=${token}` : null;

// v2 add-liquidity flow. The chain slug for Robinhood Chain is unverifiable
// (see note above), so this lands on the position-creation picker.
export const dexAddLpUrl = (token?: string) =>
    token ? `${UNI_BASE}/positions/create/v2` : null;

// Uniswap's info pages don't cover Robinhood Chain yet — point pair info
// at the Blockscout address page instead.
export const dexPairInfoUrl = (pair?: string) =>
    pair ? `${EXPLORER_ADDR}/${pair}` : null;

export const PROTOCOL = {
    totalSupply: 1_000_000_000,
    taxBps: 400,
    distribution: {
        lp:         { bps: 3000, label: "LP Stakers"       },
        pStake:     { bps: 2000, label: "PIGGY Stakers"    },
        reflection: { bps: 1500, label: "Reflection"       },
        referrals:  { bps: 1500, label: "Referrers"        },
        burn:       { bps: 1000, label: "Burn"             },
        treasury:   { bps: 1000, label: "Treasury"         },
    },
    lockTiers: [
        { days: 0,   mult: 1.0 },
        { days: 30,  mult: 1.2 },
        { days: 90,  mult: 1.5 },
        { days: 180, mult: 2.0 },
    ],
} as const;
